// Safe error messages that can be shown to users
const SAFE_ERROR_MESSAGES: Record<string, string> = {
  // Authentication
  'invalid credentials': 'Invalid username or password',
  'invalid username or password': 'Invalid username or password',
  'user not found': 'Invalid username or password',
  'unauthorized': 'Please log in to continue',
  'token expired': 'Your session has expired. Please log in again.',

  // Registration
  'user already exists': 'Username already exists. Please choose a different username.',
  'username already exists': 'Username already exists. Please choose a different username.',
  'password too short': 'Password must be at least 8 characters long.',
  'invalid email': 'Please enter a valid email address.',

  // Retrospective
  'retrospective not found': 'Retrospective not found or has been deleted.',
  'invalid invite token': 'Invalid or expired invite link.',
  'invite link has expired': 'This invite link has expired. Please ask the board owner for a new one.',
  'board is closed': 'This board has been closed and cannot be modified.',
  'removed from this board': 'You have been removed from this board by the owner and cannot rejoin.',
  'cannot rejoin': 'You have been removed from this board by the owner and cannot rejoin.',

  // Poker
  'session not found': 'Poker session not found or has been deleted.',
  'session is completed': 'This poker session has ended.',

  // General
  'validation failed': 'Please check your input and try again.',
  'not found': 'The requested resource was not found.',
  'forbidden': 'You do not have permission to perform this action.',
};

// Generic fallback messages by HTTP status code
const STATUS_CODE_MESSAGES: Record<number, string> = {
  400: 'Invalid request. Please check your input.',
  401: 'Please log in to continue.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  409: 'This action conflicts with existing data.',
  422: 'Please check your input and try again.',
  429: 'Too many requests. Please wait a moment.',
  500: 'Server error. Please try again later.',
  502: 'Server is temporarily unavailable. Please try again.',
  503: 'Service unavailable. Please try again later.',
};

interface ApiError {
  response?: {
    status: number;
    data?: {
      message?: string;
      title?: string;
      detail?: string;
      errors?: Record<string, string[]>;
    };
  };
  request?: unknown;
  message?: string;
}

export function getErrorMessage(error: ApiError, context?: string): string {
  if (!error.response && error.request) {
    return 'Cannot connect to server. Please check your internet connection.';
  }

  if (!error.response) {
    return 'An unexpected error occurred. Please try again.';
  }

  const { status, data } = error.response;

  if (context) {
    if (context === 'login' && status === 401) {
      return 'Invalid username or password. Please try again.';
    }
    if (context === 'register' && status === 409) {
      const detail = (data?.detail || data?.message || '').toLowerCase();
      if (detail.includes('email')) {
        return 'This email is already registered. If it\'s yours, use the Login tab to sign in.';
      }
      // Could be a genuine collision, or the user's own account from an earlier attempt that only
      // half-completed — so guide them to Login rather than insisting on a different username.
      return 'This username is already taken. If you already have an account, use the Login tab to sign in.';
    }
    if (context === 'join' && (status === 404 || status === 401)) {
      return 'Invalid or expired invite link. Please check the link and try again.';
    }
  }

  if (data) {
    const backendMessage = (data.detail || data.message || data.title || '').toLowerCase();

    for (const [key, safeMessage] of Object.entries(SAFE_ERROR_MESSAGES)) {
      if (backendMessage.includes(key)) {
        return safeMessage;
      }
    }

    if (data.errors && Object.keys(data.errors).length > 0) {
      const firstError = Object.values(data.errors)[0];
      if (Array.isArray(firstError) && firstError.length > 0) {
        const msg = firstError[0];
        if (msg && !msg.includes('Exception') && !msg.includes('Error') && msg.length < 100) {
          return msg;
        }
      }
    }
  }

  if (STATUS_CODE_MESSAGES[status]) {
    return STATUS_CODE_MESSAGES[status];
  }

  if (context) {
    switch (context) {
      case 'login':
        return 'Login failed. Please check your credentials.';
      case 'register':
        return 'Registration failed. Please try again.';
      case 'join':
        return 'Could not join. Please check the invite link.';
      default:
        return 'An error occurred. Please try again.';
    }
  }

  return 'An unexpected error occurred. Please try again.';
}

export function isUserError(error: ApiError): boolean {
  const status = error.response?.status;
  return status !== undefined && status >= 400 && status < 500;
}

export function isNetworkError(error: ApiError): boolean {
  return !error.response && !!error.request;
}

const errorHandler = {
  getErrorMessage,
  isUserError,
  isNetworkError,
};

export default errorHandler;
